// APPROVE_FILE_REQUEST has been moved to store/fileState/types.ts
// WorkflowMetadata import removed - it's now in documentMetadata slice

export type MetricTypeValue = 'METRIC_SQUARE' | 'METRIC_ROUND' | 'IMPERIAL';

export interface MetricType {
  name: string;
  value: MetricTypeValue;
}

/**
 * Available metric types
 */
export const MetricTypes: Record<string, MetricType> = {
  METRIC_SQUARE: { name: 'Metric, square holes', value: 'METRIC_SQUARE' },
  METRIC_ROUND: { name: 'Metric, round holes', value: 'METRIC_ROUND' },
  IMPERIAL: { name: 'Imperial', value: 'IMPERIAL' },
};

export type ScreenCalcMethodValue = 'NORMAL' | 'EFFICIENCY';

export interface ScreenCalcMethod {
  name: string;
  value: ScreenCalcMethodValue;
}

/**
 * Available screen calculation methods
 */
export const ScreenCalcMethods: Record<string, ScreenCalcMethod> = {
  NORMAL: { name: 'Normal', value: 'NORMAL' },
  EFFICIENCY: { name: 'Efficiency', value: 'EFFICIENCY' },
};

/**
 * Available print orientations
 */
export const PrintOrientations = {
  LANDSCAPE: { name: 'Landscape' },
  PORTRAIT: { name: 'Portrait' },
};

export interface PrintSizesInterface {
  px: { width: number; height: number };
  mm: { width: number; height: number };
}

export interface PrintFormatInterface {
  name: string;
  orientation: PrintOrientaionInterface;
}

export interface PrintOrientaionInterface {
  portrait: PrintSizesInterface;
  landscape: PrintSizesInterface;
}

/**
 * Every available printformat is specified here and the default values of the sizes is written in the portrait orientation (height is greater than width)
 */
export const PrintFormats: PrintFormatInterface[] = [
  {
    name: 'A4',
    orientation: {
      portrait: {
        px: { width: 540, height: 842 }, //520 842
        mm: { width: 210, height: 297 },
      },
      landscape: {
        px: { width: 842, height: 560 },
        mm: { width: 297, height: 210 },
      },
    },
  },
  {
    name: 'A3',
    orientation: {
      portrait: {
        px: { width: 720, height: 1015 }, //767 1191
        mm: { width: 297, height: 420 },
      },
      landscape: {
        px: { width: 1010, height: 725 },
        mm: { width: 420, height: 297 },
      },
    },
  },
  {
    name: 'A2',
    orientation: {
      portrait: {
        px: { width: 1100, height: 1540 }, //1191 1684
        mm: { width: 420, height: 594 },
      },
      landscape: {
        px: { width: 1480, height: 1070 },
        mm: { width: 594, height: 420 },
      },
    },
  },
  {
    name: 'Letter',
    orientation: {
      portrait: {
        px: { width: 570, height: 810 },
        mm: { width: 215.9, height: 279.4 },
      },
      landscape: {
        px: { width: 792, height: 570 },
        mm: { width: 279.4, height: 215.9 },
      },
    },
  },
  {
    name: 'Tabloid',
    orientation: {
      portrait: {
        px: { width: 792, height: 1200 },
        mm: { width: 279.4, height: 431.8 },
      },
      landscape: {
        px: { width: 1200, height: 792 },
        mm: { width: 431.8, height: 279.4 },
      },
    },
  },
];

// FileStatus has been moved to store/fileState/types.ts

/**
 * Interface for the settings slice of the state (user preferences only)
 */
export interface SettingsInterface {
  metricType: MetricType;
  oldMetricType: MetricType;
  screenCalcMethod: ScreenCalcMethod;
  printProperties: PrintPropertiesInterface;
  unitDisplay: SettingsUnitDisplayInterface;
  plantDisplay: SettingsPlantDisplayInterface;
  autoSimulate: boolean;
  canvasBodyFontSize: FontSizeInterface;
  autoSave: boolean;
}

// Moved to documentMetadata slice:
// - projectData: ProjectDataInterface
// - flowsheetMetadata?: WorkflowMetadata

// Moved to fileState slice:
// - fileStatus: FileStatus
// - fileHasBeenEdited: boolean

// Moved to layout slice:
// - simulating: boolean
// - showApproveFileModal: boolean
// - skipsDiagramUpdate: boolean

export interface FontSizeInterface {
  size: number;
  limit: number;
}

/**
 * Interface for the print properties in the settingsState
 */
export interface PrintPropertiesInterface {
  format: string;
  orientation: string;
  sizes: PrintSizesInterface;
  width: number;
  height: number;
  displayDraft: boolean;
}

/**
 * Interface for the project data in the settingsState
 */
export interface ProjectDataInterface {
  currentDate: string;
  lastSimulation: string;
  customer: string;
  projectLineOne: string;
  projectLineTwo: string;
  projectNo: string;
  flowSheetNo: string;
  drawnBy: string;
  revisedBy: string;
  customLogo: string;
  approvedBy: string;
  userName: string;
}

/**
 * Interface for the unit display options in the settingsState
 */
export interface SettingsUnitDisplayInterface {
  warningText: boolean;
  showLoad: boolean;
  showEquipmentType: boolean;
  showUnitNumber: boolean;
  showRiskLevel: boolean;
}

/**
 * Interface for the plant display options in the settingsState
 */
export interface SettingsPlantDisplayInterface {
  massFlows: boolean;
  productPercentage: boolean;
  showMinorWarnings: boolean;
  directionalArrows: boolean;
  drawingSheet: boolean;
  reverseSizeGradiation: boolean;
  bottleNeck: boolean;
  showGrid: boolean;
}

/**
 * Interface for the chamber analysis display options in the settingsState
 */
export interface SettingsChamberAnalysisDisplayInterface {
  showFeedSize: boolean;
  showCompressionZones: boolean;
}

// ApproveFileRequestActionInterface has been moved to store/fileState/types.ts
